import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Loader2, RefreshCw, AlertTriangle, AlertCircle, Info,
  ExternalLink, Clock, CheckCircle2, ChevronDown, ChevronUp,
  Play,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { DashboardLayout } from '../components/DashboardLayout';
import { attentionAPI } from '../lib/api';
import { toast } from 'sonner';

const SEVERITY_CONFIG = {
  critical: {
    icon: AlertCircle,
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-950/30',
    border: 'border-red-200 dark:border-red-800',
    badge: 'destructive',
    label: 'Critical',
  },
  warning: {
    icon: AlertTriangle,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    border: 'border-amber-200 dark:border-amber-800',
    badge: 'outline',
    label: 'Warning',
  },
  info: {
    icon: Info,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    border: 'border-blue-200 dark:border-blue-800',
    badge: 'secondary',
    label: 'Info',
  },
};

function relativeTime(isoDate) {
  if (!isoDate) return 'Unknown';
  const diff = Date.now() - new Date(isoDate).getTime();
  const hours = diff / 3600000;
  if (hours < 1) return `${Math.round(hours * 60)}m ago`;
  if (hours < 24) return `${Math.round(hours)}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function FindingCard({ finding }) {
  const config = SEVERITY_CONFIG[finding.severity] || SEVERITY_CONFIG.info;
  const Icon = config.icon;

  return (
    <div className={`rounded-lg border ${config.border} ${config.bg} p-4`}>
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 ${config.color} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`text-sm font-medium ${config.color}`}>
              {finding.title}
            </p>
            {finding.entity && (
              <Badge variant="outline" className="text-xs">
                {finding.entity}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {finding.detail}
          </p>
          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              First seen: {relativeTime(finding.first_seen)}
            </span>
            <span>
              Last seen: {relativeTime(finding.last_seen)}
            </span>
            <span className="text-xs opacity-60">
              {finding.check}
            </span>
          </div>
        </div>
        {finding.action_url && (
          <a
            href={finding.action_url}
            className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            title="Go to action surface"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
}

function SeveritySection({ severity, findings }) {
  const [collapsed, setCollapsed] = useState(false);
  const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.info;

  if (!findings || findings.length === 0) return null;

  return (
    <div>
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-2 mb-3 w-full text-left"
      >
        <Badge variant={config.badge}>
          {config.label} ({findings.length})
        </Badge>
        {collapsed ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {!collapsed && (
        <div className="space-y-3">
          {findings.map((f) => (
            <FindingCard key={f.finding_key} finding={f} />
          ))}
        </div>
      )}
    </div>
  );
}

function ResolvedSection({ findings }) {
  const [expanded, setExpanded] = useState(false);

  if (!findings || findings.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 w-full text-left"
        >
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Recently Resolved ({findings.length})
          </CardTitle>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
      </CardHeader>
      {expanded && (
        <CardContent>
          <div className="space-y-2">
            {findings.map((f) => (
              <div
                key={f.finding_key}
                className="flex items-center gap-3 py-2 border-b last:border-0 text-sm"
              >
                <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-muted-foreground line-through">{f.title}</span>
                  <span className="text-xs text-muted-foreground ml-2">
                    Resolved {relativeTime(f.resolved_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function DigestPanel({ digest }) {
  if (!digest) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Daily Digest</CardTitle>
      </CardHeader>
      <CardContent>
        <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-mono bg-muted/50 rounded p-3">
          {digest.digest_text}
        </pre>
        {digest.last_run && (
          <p className="text-xs text-muted-foreground mt-2">
            Last run: {digest.last_run.finished_at || 'In progress'}
            {digest.last_run.errors?.length > 0 && (
              <span className="text-red-500 ml-2">
                ({digest.last_run.errors.length} errors)
              </span>
            )}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export function AttentionPage() {
  const [data, setData] = useState(null);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  // ?check=<name> lists every row of one check (the overview groups
  // per-payment checks such as "paid with no document" into one line).
  const [searchParams] = useSearchParams();
  const checkFilter = searchParams.get('check') || undefined;

  const fetchData = useCallback(async () => {
    try {
      const [findingsRes, digestRes] = await Promise.all([
        attentionAPI.getFindings({ include_resolved: true, check: checkFilter, limit: checkFilter ? 500 : 200 }),
        attentionAPI.getDigest(),
      ]);
      setData(findingsRes.data);
      setDigest(digestRes.data);
    } catch (err) {
      toast.error('Failed to load attention data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [checkFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRunChecks = async () => {
    setRunning(true);
    try {
      const res = await attentionAPI.runChecks();
      toast.success(
        `Checks complete: ${res.data.total_findings} findings, ${res.data.total_resolved} resolved`
      );
      fetchData();
    } catch (err) {
      toast.error('Failed to run checks');
      console.error(err);
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  const open = data?.open || {};
  const totalOpen = data?.open_total || 0;

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Attention</h1>
            <p className="text-muted-foreground mt-1">
              {totalOpen} open {totalOpen === 1 ? 'finding' : 'findings'} requiring attention
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRunChecks}
              disabled={running}
            >
              {running ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run Checks
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary badges */}
        <div className="flex items-center gap-3">
          {(open.critical?.length > 0) && (
            <Badge variant="destructive">
              {open.critical.length} Critical
            </Badge>
          )}
          {(open.warning?.length > 0) && (
            <Badge variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-400">
              {open.warning.length} Warning
            </Badge>
          )}
          {(open.info?.length > 0) && (
            <Badge variant="secondary">
              {open.info.length} Info
            </Badge>
          )}
          {totalOpen === 0 && (
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-5 w-5" />
              <span className="text-sm font-medium">All clear</span>
            </div>
          )}
        </div>

        {/* Findings by severity */}
        <div className="space-y-6">
          <SeveritySection severity="critical" findings={open.critical} />
          <SeveritySection severity="warning" findings={open.warning} />
          <SeveritySection severity="info" findings={open.info} />
        </div>

        {/* Resolved */}
        <ResolvedSection findings={data?.resolved} />

        {/* Digest */}
        <DigestPanel digest={digest} />
      </div>
    </DashboardLayout>
  );
}
